
import { Component, Vue, Prop } from "vue-property-decorator";
import axios from "axios";
import { CDataTableFields } from "@/@types";
import { authTokenStorage } from "@/auth/AuthTokenStorage";
import { DateFormatMixin } from "@/mixins";
import { mixins } from "vue-class-component";

interface BalanceTransaction {
  created: number;
  description: string | null;
  amount: number;
  fee: number;
  net: number;
  type: string;
  charge_details?: {
    payment_intent?: string;
    metadata?: Record<string, any>;
  };
}

interface Payout {
  id: string;
  amount: number;
  status: string;
  arrival_date_mst_formatted: string;
  balance_transactions?: BalanceTransaction[];
}

interface PayoutDetailResponse {
  success: boolean;
  payout: Payout;
}

@Component({
  components: { }
})
export default class StripePayoutDetail extends mixins(DateFormatMixin) {
  loading = true;
  payout: Payout | null = null;
  expandedMetadata: Record<string, boolean> = {};

  @Prop([String, Number])
  readonly id!: string | number;

  transactionFields: CDataTableFields = [
    { key: "date", label: "Date", filter: false, sorter: true },
    { key: "description", label: "Description", filter: false, sorter: true },
    { key: "amount", label: "Amount", filter: false, sorter: true },
    { key: "fee", label: "Fee", filter: false, sorter: true },
    { key: "net", label: "Net", filter: false, sorter: true },
    { key: "payment_intent", label: "Payment Intent #", filter: false, sorter: false },
    { key: "metadata", label: "Metadata", filter: false, sorter: false },
  ];

  get balanceTransactions(): BalanceTransaction[] {
    return this.payout?.balance_transactions || [];
  }

  async mounted() {
    await this.fetchPayoutDetail();
  }

  async fetchPayoutDetail() {
    this.loading = true;
    try {
      const token = await authTokenStorage.getAccessToken();
      const response = await axios.get<PayoutDetailResponse>(
        `https://payments.api.kbmercury.com/stripe/payout/detail/${this.id}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (response.data.success && response.data.payout) {
        this.payout = response.data.payout;
      }
    } catch (error) {
      console.error("Error fetching payout detail:", error);
    } finally {
      this.loading = false;
    }
  }

  formatAmount(amount: number): string {
    if (amount === null || amount === undefined) return '$0.00';
    const value = amount / 100;
    const formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `$${formatted}`;
  }

  formatStatus(status: string): string {
    if (!status) return '';
    
    const text = status.replace(/_/g, ' ');
    const words = text.split(' ');
    if (words.length > 0) {
      words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
      for (let i = 1; i < words.length; i++) {
        words[i] = words[i].toLowerCase();
      }
    }
    
    return words.join(' ');
  }

  getStatusColor(status: string): string {
    if (!status) return 'secondary';
    
    const normalizedStatus = status.toLowerCase();
    
    if (normalizedStatus === 'in_transit' || normalizedStatus === 'in transit') {
      return 'info';
    }
    
    if (normalizedStatus === 'paid') {
      return 'success';
    }
    
    return 'secondary';
  }

  getPaymentIntent(item: BalanceTransaction): string {
    if (item.type === 'charge' && item.charge_details?.payment_intent) {
      return item.charge_details.payment_intent;
    }
    return 'N/A';
  }

  getMetadataText(item: BalanceTransaction): string {
    if (item.type === 'charge' && item.charge_details?.metadata) {
      const metadata = item.charge_details.metadata;
      if (typeof metadata === 'object' && metadata !== null) {
        return Object.entries(metadata)
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ');
      }
      return String(metadata);
    }
    return '';
  }

  getMetadataFormatted(item: BalanceTransaction): string {
    if (item.type === 'charge' && item.charge_details?.metadata) {
      const metadata = item.charge_details.metadata;
      if (typeof metadata === 'object' && metadata !== null) {
        // Format each key-value pair with bold keys and line breaks
        return Object.entries(metadata)
          .map(([key, value]) => `<strong>${key}</strong>: ${value}`)
          .join(',\n');
      }
    }
    return '';
  }

  getMetadataFormattedSingleLine(item: BalanceTransaction): string {
    if (item.type === 'charge' && item.charge_details?.metadata) {
      const metadata = item.charge_details.metadata;
      if (typeof metadata === 'object' && metadata !== null) {
        // Format with bold keys but single line
        return Object.entries(metadata)
          .map(([key, value]) => `<strong>${key}</strong>: ${value}`)
          .join(', ');
      }
    }
    return '';
  }

  hasMultipleLines(item: BalanceTransaction): boolean {
    const text = this.getMetadataText(item);
    if (!text) return false;
    // Check if there's a comma (indicating multiple key-value pairs)
    return text.indexOf(',') !== -1;
  }

  getMetadataFirstLine(item: BalanceTransaction): string {
    if (item.type === 'charge' && item.charge_details?.metadata) {
      const metadata = item.charge_details.metadata;
      if (typeof metadata === 'object' && metadata !== null) {
        const entries = Object.entries(metadata);
        if (entries.length > 0) {
          // Format first key-value pair with bold key
          const [key, value] = entries[0];
          return `<strong>${key}</strong>: ${value}`;
        }
      }
    }
    return '';
  }

  getTransactionKey(item: BalanceTransaction, index: number): string {
    // Use a combination of fields to create a unique key, or fall back to index
    if (item.created && item.amount !== undefined) {
      return `${item.created}-${item.amount}-${index}`;
    }
    return `transaction-${index}`;
  }

  isMetadataExpanded(transactionKey: string): boolean {
    return !!this.expandedMetadata[transactionKey];
  }

  toggleMetadata(transactionKey: string): void {
    this.$set(this.expandedMetadata, transactionKey, !this.expandedMetadata[transactionKey]);
  }
}
